<template>
    <div class="kb-config-synthflow">
        <section class="kb-config-synthflow__section">
            <h4 class="kb-config-synthflow__section-title">
                {{ $t('knowledgebase.channelSynthflowSectionConnection') }}
            </h4>
            <p class="kb-config-synthflow__section-help">
                {{ $t('knowledgebase.channelSynthflowSectionConnectionHelp') }}
            </p>
            <div class="field">
                <label class="label">{{ $t('knowledgebase.channelSynthflowInstanceUrl') }}</label>
                <input
                    class="input"
                    type="text"
                    v-model="instanceUrl"
                    placeholder="https://api.eu.synthflow.ai"
                    @input="emit"
                />
                <p class="help">{{ $t('knowledgebase.channelSynthflowInstanceUrlHelp') }}</p>
            </div>
            <div class="field">
                <label class="label">{{ $t('knowledgebase.channelSynthflowApiKey') }}</label>
                <secret-input
                    v-model="apiKey"
                    :placeholder="$t('knowledgebase.channelApiKeyNewPlaceholder')"
                    @input="emit"
                />
                <p class="help">{{ $t('knowledgebase.channelSynthflowApiKeyHelp') }}</p>
            </div>
            <div class="field">
                <label class="label">{{ $t('knowledgebase.channelSynthflowAssistantId') }}</label>
                <input
                    class="input"
                    type="text"
                    v-model="assistantId"
                    placeholder="0828b266-7c12-4c0c-9c2e-9014467ec5a8"
                    @input="emit"
                />
                <p class="help">{{ $t('knowledgebase.channelSynthflowAssistantIdHelp') }}</p>
            </div>
            <div v-if="connection" class="kb-config-synthflow__sync">
                <template v-if="resyncScheduled">
                    <span class="kb-config-synthflow__sync-pending">
                        {{ $t('knowledgebase.channelResyncScheduled') }}
                    </span>
                </template>
                <template v-else>
                    <template v-if="connection.last_sync_error">
                        <strong class="kb-config-synthflow__sync-failed">
                            {{ $t('knowledgebase.channelLastSyncFailed') }}
                        </strong>
                        <span class="kb-config-synthflow__sync-error">{{ connection.last_sync_error }}</span>
                    </template>
                    <template v-else-if="connection.last_synced_at">
                        <span class="kb-config-synthflow__sync-ok">
                            {{ $t('knowledgebase.channelLastSynced') }}
                            <kb-time :value="connection.last_synced_at"/>
                        </span>
                    </template>
                    <template v-else>
                        <span class="kb-config-synthflow__sync-pending">
                            {{ $t('knowledgebase.channelNeverSynced') }}
                        </span>
                    </template>
                    <button
                        type="button"
                        class="button is-small is-outlined kb-config-synthflow__sync-retry"
                        :disabled="resyncPending"
                        @click="resync"
                    >
                        {{ $t('knowledgebase.channelResync') }}
                    </button>
                </template>
            </div>
        </section>

        <section class="kb-config-synthflow__section">
            <h4 class="kb-config-synthflow__section-title">
                {{ $t('knowledgebase.channelSynthflowSectionData') }}
            </h4>
            <p class="kb-config-synthflow__section-help">
                {{ $t('knowledgebase.channelSynthflowSectionDataHelp') }}
            </p>

            <!-- KB: the primary data feed. Read-only summary in the configure
                 flow; hidden during connect because there's nothing to summarise
                 yet (no connection id, no exclusions). -->
            <div v-if="summary" class="kb-config-synthflow__subsection">
                <h5 class="kb-config-synthflow__sub-title">
                    {{ $t('knowledgebase.channelSynthflowKbHeading') }}
                </h5>
                <div class="kb-config-synthflow__kb-summary">
                    <div>
                        <span class="kb-config-synthflow__kb-count">{{ summary.visibleCount }}</span>
                        {{ $t('knowledgebase.channelSynthflowKbVisible', {total: summary.totalPublished}) }}
                        <span v-if="summary.excludedCount > 0" class="kb-config-synthflow__kb-hidden">
                            · {{ $tc('knowledgebase.channelHiddenCount', summary.excludedCount, {count: summary.excludedCount}) }}
                        </span>
                    </div>
                    <div
                        v-if="summary.pendingVisibilityCount > 0"
                        class="kb-config-synthflow__kb-pending"
                    >
                        {{ $tc('knowledgebase.channelSynthflowKbPending', summary.pendingVisibilityCount, {count: summary.pendingVisibilityCount}) }}
                    </div>
                </div>
            </div>

            <!-- Complementary structured data on top of the KB. -->
            <div class="kb-config-synthflow__subsection">
                <h5 class="kb-config-synthflow__sub-title">
                    {{ $t('knowledgebase.channelSynthflowIncludeHeading') }}
                </h5>
                <p class="kb-config-synthflow__sub-help">
                    {{ $t('knowledgebase.channelSynthflowIncludeHelp') }}
                </p>
                <label
                    v-for="key in includeKeys"
                    :key="key"
                    class="checkbox kb-config-synthflow__include-item"
                >
                    <input type="checkbox" v-model="include[key]" @change="emit"/>
                    {{ $t('knowledgebase.channelSynthflowInclude' + pascalCase(key)) }}
                </label>
            </div>

            <!-- Portal scopes inventory + draft-booking attribution. Important
                 but the operator picks it once and rarely revisits, so it sits
                 below the more-frequently-touched content controls. -->
            <div class="kb-config-synthflow__subsection">
                <h5 class="kb-config-synthflow__sub-title">
                    {{ $t('knowledgebase.channelSynthflowPortal') }}
                </h5>
                <div class="field">
                    <div class="select">
                        <select v-model="portalId" @change="emit">
                            <option :value="null">{{ $t('knowledgebase.channelSynthflowPortalPlaceholder') }}</option>
                            <option v-for="p in portals" :key="p.id" :value="p.id">{{ p.name }}</option>
                        </select>
                    </div>
                    <p class="help">
                        {{ $t('knowledgebase.channelSynthflowPortalHelp') }}
                        <router-link :to="{name: 'portals'}">
                            {{ $t('knowledgebase.channelSynthflowPortalManageLink') }}
                        </router-link>
                    </p>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
// Per-integration_type config form for Synthflow (and its white-labels).
// Owns its own UI state, emits the merged config object so the parent modal
// only needs to know "give me a config dict for this channel" — not what the
// fields are.
//
// Adding a new integration_type = new sibling component. The parent modal
// looks up the right one by integration_type code; if none matches, falls
// back to a generic "no config needed" placeholder.
//
// `portal_id` is emitted alongside config fields and the parent modal lifts
// it to the top-level `portal` field on the connection — it's a real FK, not
// part of `config`.

import Api from '../../../api/Api';
import AppModel from '../../../models/AppModel';
import SecretInput from '../../components/secret-input/secret-input.vue';
import KbTime from './kb-time.vue';

const INCLUDE_KEYS = [
    'settings_campground',
    'settings_rules',
    'settings_locktimes',
    'additional_costs',
    'guests',
    'cancellation_terms',
];

function defaultInclude(value) {
    const out = {};
    for(const key of INCLUDE_KEYS) {
        const v = value && value['include_' + key];
        out[key] = v === undefined ? true : !!v;
    }
    return out;
}

export default {
    name: 'kb-config-synthflow',

    components: {SecretInput, KbTime},

    props: {
        // For Configure: the existing config blob from the server merged with
        // the connection's top-level `portal` id. For Connect: empty object.
        value: {type: Object, default: () => ({})},
        // The full connection row from the server, for last_sync_error /
        // last_synced_at. null during the connect flow.
        connection: {type: Object, default: null},
        // Parent-computed campground summary: visible / total / pending counts.
        // null during the connect flow.
        summary: {type: Object, default: null},
    },

    data() {
        return {
            instanceUrl: this.value.instance_url || '',
            apiKey: this.value.api_key || '',
            assistantId: this.value.assistant_id || '',
            portalId: this.value.portal_id == null ? null : this.value.portal_id,
            include: defaultInclude(this.value),
            includeKeys: INCLUDE_KEYS,
            // The "queued, come back in a minute" acknowledgement is local to
            // this open of the modal — no polling, no global state. The
            // backend clears last_sync_error synchronously so when the user
            // refreshes, the channel card already reflects the new state.
            resyncPending: false,
            resyncScheduled: false,
        };
    },

    computed: {
        portals() {
            return AppModel.portals || [];
        },
    },

    methods: {
        pascalCase(snake) {
            return snake.split('_').map(s => s[0].toUpperCase() + s.slice(1)).join('');
        },
        async resync() {
            if(!this.connection || this.resyncPending) return;
            this.resyncPending = true;
            try {
                await Api.knowledgebase.resyncConnection(this.connection.id);
                this.resyncScheduled = true;
            } finally {
                this.resyncPending = false;
            }
        },
        emit() {
            const payload = {
                instance_url: this.instanceUrl.trim(),
                api_key: this.apiKey,
                assistant_id: this.assistantId.trim(),
                portal_id: this.portalId,
            };
            for(const key of INCLUDE_KEYS) {
                payload['include_' + key] = !!this.include[key];
            }
            this.$emit('input', payload);
        },
    },
};
</script>

<style lang="scss" scoped>
.kb-config-synthflow {
    // Two-column layout once the modal grows wide enough to fit it. The
    // parent modal switches its own width based on integration_type;
    // 720px here corresponds to the wider modal minus padding.
    @media (min-width: 720px) {
        display: grid;
        grid-template-columns: 1fr 1px 1fr;
        column-gap: 28px;
    }

    &__section + &__section {
        // Mobile / narrow: stack with a horizontal divider.
        margin-top: 28px;
        padding-top: 24px;
        border-top: 1px solid #eee;
    }

    @media (min-width: 720px) {
        &__section + &__section {
            margin-top: 0;
            padding-top: 0;
            border-top: none;
        }

        &__section:nth-of-type(2)::before {
            content: '';
            display: block;
            position: absolute;
            top: 0;
            bottom: 0;
            left: -14px;
            width: 1px;
            background: #eee;
        }
        &__section:nth-of-type(2) {
            position: relative;
            grid-column: 3;
        }
    }

    &__section-title {
        font-size: 1.15rem;
        font-weight: 700;
        color: #222;
        margin: 0 0 4px;
        letter-spacing: -0.01em;
    }

    &__section-help {
        color: #777;
        font-size: 0.85rem;
        margin: 0 0 20px;
    }

    // Subsections inside Campground data: KB summary / Share with the bot /
    // Portal. No divider — these are conceptually one section, just visually
    // chunked by their own titles.
    &__subsection + &__subsection {
        margin-top: 22px;
    }

    &__sub-title {
        font-size: 0.95rem;
        font-weight: 700;
        color: #333;
        margin: 0 0 6px;
    }

    &__sub-help {
        color: #777;
        font-size: 0.82rem;
        margin: 0 0 10px;
    }

    .field:not(:last-child) {
        margin-bottom: 16px;
    }

    // KB summary block — bigger emphasis since this is the bot's primary feed.
    &__kb-summary {
        font-size: 0.95rem;
        color: #333;
    }

    &__kb-count {
        font-weight: 700;
        color: #222;
        font-size: 1.1rem;
    }

    &__kb-hidden {
        color: #a76;
    }

    &__kb-pending {
        color: #b80;
        font-size: 0.85rem;
        margin-top: 2px;
    }

    &__sync {
        font-size: 0.82rem;
        color: #888;
        margin-top: 10px;
    }

    &__sync-ok {
        color: #4a7;
    }

    &__sync-pending {
        color: #888;
        font-style: italic;
    }

    &__sync-failed {
        color: #c33;
    }

    &__sync-error {
        margin-left: 6px;
        color: #c33;
        word-break: break-word;
    }

    &__sync-retry {
        display: block;
        margin-top: 8px;
    }

    &__include-item {
        display: block;
        margin-top: 6px;
        font-weight: normal;
    }
}
</style>



// WEBPACK FOOTER //
// src/views/pages/knowledge-base/kb-config-synthflow.vue